import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Manufacturer, Model, Motor } from '../types'
import * as api from '../api/tecdocApi'
import type { ApiError } from '../types/api'

export const useSelectionStore = defineStore('selection', () => {
  const manufacturers = ref<Manufacturer[]>([])
  const models = ref<Model[]>([])
  const motors = ref<Motor[]>([])

  const selectedManufacturerId = ref<number | null>(null)
  const selectedModelId = ref<number | null>(null)
  const selectedMotorId = ref<number | null>(null)

  const isLoadingManufacturers = ref(false)
  const isLoadingModels = ref(false)
  const isLoadingMotors = ref(false)
  const error = ref<string | null>(null)

  async function loadManufacturers() {
    isLoadingManufacturers.value = true
    error.value = null

    try {
      manufacturers.value = await api.getManufacturers()
    } catch (err) {
      error.value = (err as ApiError).message
    } finally {
      isLoadingManufacturers.value = false
    }
  }

  async function selectManufacturer(manuId: number) {
    selectedManufacturerId.value = manuId
    selectedModelId.value = null
    selectedMotorId.value = null
    models.value = []
    motors.value = []
    isLoadingModels.value = true
    error.value = null

    try {
      models.value = await api.getModels(manuId)
    } catch (err) {
      error.value = (err as ApiError).message
    } finally {
      isLoadingModels.value = false
    }
  }

  async function selectModel(modelId: number) {
    selectedModelId.value = modelId
    selectedMotorId.value = null
    motors.value = []
    isLoadingMotors.value = true
    error.value = null

    try {
      motors.value = await api.getMotors(selectedManufacturerId.value!, modelId)
    } catch (err) {
      error.value = (err as ApiError).message
    } finally {
      isLoadingMotors.value = false
    }
  }

  function selectMotor(carId: number) {
    selectedMotorId.value = carId
  }

  function reset() {
    manufacturers.value = []
    models.value = []
    motors.value = []
    selectedManufacturerId.value = null
    selectedModelId.value = null
    selectedMotorId.value = null
    error.value = null
  }

  return {
    manufacturers,
    models,
    motors,
    selectedManufacturerId,
    selectedModelId,
    selectedMotorId,
    isLoadingManufacturers,
    isLoadingModels,
    isLoadingMotors,
    error,
    loadManufacturers,
    selectManufacturer,
    selectModel,
    selectMotor,
    reset,
  }
})
